import { useEffect, useState } from "react";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";

/**
 * "Download Catalogue" button behaviour:
 * - 0 brochures: shows a friendly toast (lead-capture fallback).
 * - 1 brochure: opens that PDF directly in a new tab.
 * - 2+ brochures: scrolls to the #catalogues section so the user picks one.
 */
export default function BrochureDownloadButton({
  className = "btn-accent",
  label = "Download Product Catalogue",
  testId = "brochure-download-btn",
}) {
  const [brochures, setBrochures] = useState([]);

  useEffect(() => {
    api
      .get("/brochures")
      .then((r) => setBrochures(r.data || []))
      .catch(() => setBrochures([]));
  }, []);

  const handle = () => {
    if (brochures.length === 0) {
      toast.message(
        "Catalogue available on request — please share your details on the Contact page and we'll email the latest PDF."
      );
      return;
    }

    if (brochures.length === 1) {
      const b = brochures[0];
      const url = b.file_url.startsWith("http")
        ? b.file_url
        : `${API.replace(/\/api$/, "")}${b.file_url}`;
      window.open(url, "_blank", "noopener");
      return;
    }

    // Multiple catalogues — scroll to the picker section
    const section = document.getElementById("catalogues");
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      // Fallback if section not present on the current page
      window.location.href = "/products#catalogues";
    }
  };

  const Icon = brochures.length === 0 ? FileText : Download;

  return (
    <button
      type="button"
      onClick={handle}
      data-testid={testId}
      className={`${className} inline-flex items-center gap-2`}
    >
      <Icon size={18} />
      {label}
      {brochures.length > 1 && (
        <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-white/25 text-[11px] font-bold">
          {brochures.length}
        </span>
      )}
    </button>
  );
}
